import React, { KeyboardEvent, MouseEvent, useMemo } from "react";
import { ButtonWithIcon } from "../buttons/button-with-icon/button-with-icon";
import { IconButton } from "../buttons/icon-button/icon-button";

import documentCheckIcon from "@src/assets/svg/document-check-icon.svg";
import uploadIcon from "@src/assets/svg/upload-icon.svg";
import deleteGrayIcon from "@src/assets/svg/delete-gray-icon.svg";
import styles from "./drop-area-add-image.module.scss";
import { useDropzone } from "react-dropzone";
import {
  acceptStyle,
  rejectStyle,
  stylesDropArea,
} from "./drop-area-add-image-dynamic-styles";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N } from "@src/language/language-context";

const cssClassName = "drop-area-add-image";

const acceptedImageFileTypes = {
  "image/svg+xml": [".svg"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

interface DropAreaAddImageTypes<T, K extends keyof T> {
  label: string;
  values: T;
  keyProp: K;
  setValues: (values: T) => void;
}

/** Drop area for an image file. Stores the raw `File` (not base64) on
 *  `values[keyProp]` so a caller can append it to a multipart FormData PUT
 *  body. The file name is mirrored to `values[\`${keyProp}FileName\`]` for
 *  display. Accepted types: SVG, JPG, PNG. */
export const DropAreaAddImage = <T, K extends keyof T>({
  label,
  values,
  keyProp,
  setValues,
}: DropAreaAddImageTypes<T, K>) => {
  const i18n = useI18N();
  const texts = {
    uploadOrDragAndDropText: i18n(
      "modules.dropAreaAddImage.uploadOrDragAndDropText",
    ),
  };
  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      accept: acceptedImageFileTypes,
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length === 1) {
          setValues({
            ...values,
            [keyProp]: acceptedFiles[0] as T[K],
            [`${String(keyProp)}FileName`]: acceptedFiles[0].name as string,
          });
        }
      },
    });

  const styleDropArea = useMemo(
    () => ({
      ...stylesDropArea,
      ...(isDragAccept ? acceptStyle : {}),
      ...((values as unknown as Record<string, unknown>)[keyProp as string]
        ? acceptStyle
        : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragReject, keyProp, values],
  );

  const deleteUploadedFile = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setValues({
      ...values,
      [keyProp]: null as T[K],
      [`${String(keyProp)}FileName`]: "" as string,
    });
  };

  const currentFileName = (values as unknown as Record<string, string>)[
    `${String(keyProp)}FileName`
  ];

  return (
    <div className={styles[cssClassName]}>
      <label className={`${styles[`${cssClassName}__label`]}`} htmlFor={label}>
        {label}
      </label>
      <div
        {...getRootProps({
          style: styleDropArea,
        })}
      >
        <input {...getInputProps()} id={label} />
        {(values as unknown as Record<string, unknown>)[keyProp as string] !=
        null ? (
          <FlexRowContainer justifyContent="space-between">
            <ButtonWithIcon
              type="button"
              fun={() => ({})}
              icon={documentCheckIcon}
              variant={["no-outline", "color-grey", "font-thin"]}
              text={currentFileName}
            />
            <IconButton fun={deleteUploadedFile} icon={deleteGrayIcon} />
          </FlexRowContainer>
        ) : (
          <>
            <IconButton fun={() => ({})} icon={uploadIcon} />
            <p className={`${styles[`${cssClassName}__label`]}`}>
              {texts.uploadOrDragAndDropText}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
